import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getRoleLandingPath } from '@/lib/roleLanding';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Scissors, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);
      toast.success('Welcome back!');
      
      navigate(getRoleLandingPath(userData?.role), { replace: true });
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (!error?.response || [500, 502, 503, 504, 520, 522, 524].includes(status)) {
        toast.error('Server is waking up or temporarily unavailable. Please retry in a few seconds.');
      } else {
        toast.error(detail || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left side - Image with Navy overlay */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1611171711960-cc3da04c95bf?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-[#1e3a5f]/85" />
        <div className="relative z-10 flex flex-col justify-between p-12 h-full">
          {/* Top Logo */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-500">
              <Scissors className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                Perfect Attire
              </h2>
              <p className="text-[11px] text-white/40 tracking-widest uppercase">Since 2024</p>
            </div>
          </div>

          {/* Bottom Quote */}
          <blockquote className="space-y-4">
            <p className="text-2xl font-light text-white/90 leading-relaxed">
              "The details are not the details.<br/>They make the design."
            </p>
            <footer className="text-orange-400 text-sm tracking-widest uppercase font-medium">
              — Charles Eames
            </footer>
          </blockquote>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-card">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center space-y-2">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-orange-500 mb-4">
              <Scissors className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
          </div>

          {/* Header */}
          <div className="text-center lg:text-left space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Sign in</h2>
            <p className="text-sm text-muted-foreground">
              Sign in to access your bespoke management dashboard
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="h-9 bg-background border-input focus:border-primary focus:ring-1 focus:ring-primary rounded-md"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="h-9 bg-background border-input focus:border-primary focus:ring-1 focus:ring-primary rounded-md pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-9 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-medium"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground">
            By continuing, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
